<template>
  <div class="applications-landing">
    <!-- Hero Banner -->
    <banner
      :backgroundImage="$maxxpm.config.home.banner.image"
      class="hero-banner"
    >
      <div class="hero-content">
        <img
          :src="$maxxpm.config.logo.src"
          :alt="$maxxpm.config.logo.alt"
          class="hero-logo"
        />
        <h1 class="hero-title">Apply Today</h1>
        <h2 class="hero-subtitle">Start Your Journey to Luxury Living</h2>
        <p class="hero-description">Simple, secure application process - Get approved and move in faster</p>
        <Button
          label="Start Your Application"
          class="hero-cta-button"
          @click="scrollToAvailableUnits"
          rounded
          size="large"
        />
      </div>
    </banner>

    <!-- Available Units Section -->
    <div
      id="available-units"
      class="container mt-5 mb-5 available-units-section"
      v-if="postedUnits.length > 0"
    >
      <h2 class="text-primary text-center mb-4">Available Units</h2>
      <p class="text-center mb-4 available-units-description">Browse our available units below and select one to apply.</p>
      <div class="listings-container">
        <div
          v-for="unit in postedUnits"
          :key="unit.id"
          class="unit-card"
        >
          <div
            class="unit-image-container"
            @click.stop="openPhotoModal(unit)"
          >
            <img
              v-if="unit.unit_photos && unit.unit_photos.length > 0"
              :src="getPhotoUrl(unit.unit_photos[0])"
              :alt="unit.address1 || 'Unit photo'"
              class="unit-image"
            />
            <div
              v-else
              class="unit-image-placeholder"
            >
              <i class="fas fa-home"></i>
              <p>No photo available</p>
            </div>
            <div class="unit-image-overlay">
              <i class="fas fa-images"></i>
              <span v-if="unit.unit_photos">{{ unit.unit_photos.length }} photo{{ unit.unit_photos.length > 1 ? "s" : "" }}</span>
            </div>
          </div>
          <div class="unit-info">
            <h3 class="text-primary">{{ formatAddress(unit) }}</h3>
            <div class="unit-details">
              <div
                v-if="unit.bedrooms"
                class="unit-detail"
              >
                <i class="fas fa-bed"></i>
                <span>{{ unit.bedrooms }} Bed{{ unit.bedrooms > 1 ? "s" : "" }}</span>
              </div>
              <div
                v-if="unit.bathrooms"
                class="unit-detail"
              >
                <i class="fas fa-bath"></i>
                <span>{{ unit.bathrooms }} Bath{{ unit.bathrooms > 1 ? "s" : "" }}</span>
              </div>
              <div
                v-if="unit.square_feet"
                class="unit-detail"
              >
                <i class="fas fa-ruler-combined"></i>
                <span>{{ unit.square_feet.toLocaleString() }} sq ft</span>
              </div>
            </div>
            <div
              v-if="unit.listed_rent"
              class="unit-rent"
            >
              <span class="rent-amount">${{ unit.listed_rent.toLocaleString() }}</span>
              <span class="rent-period">/month</span>
            </div>
            <Button
              label="Apply Now"
              class="apply-button"
              @click.stop="openApplicationUrl(unit)"
              severity="primary"
              rounded
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      class="container mt-5 mb-5"
      v-if="loadingUnits"
    >
      <div class="text-center">
        <i class="pi pi-spin pi-spinner units-loading"></i>
        <p>Loading available units...</p>
      </div>
    </div>

    <!-- Photo Modal -->
    <Dialog
      v-model:visible="photoModalVisible"
      :modal="true"
      :dismissableMask="true"
      :style="{ width: '90vw', maxWidth: '1200px' }"
      :breakpoints="{ '960px': '95vw', '641px': '95vw' }"
      class="photo-modal"
      @hide="closePhotoModal"
    >
      <template #header>
        <h2 class="text-primary">
          {{ selectedUnit ? formatAddress(selectedUnit) : "Unit Photos" }}
        </h2>
      </template>
      <div
        v-if="selectedUnit && selectedUnit.unit_photos && selectedUnit.unit_photos.length > 0"
        class="photo-gallery"
      >
        <div class="main-photo-container">
          <img
            :src="getPhotoUrl(selectedUnit.unit_photos[currentPhotoIndex])"
            :alt="`Photo ${currentPhotoIndex + 1}`"
            class="main-photo"
          />
          <button
            v-if="selectedUnit.unit_photos.length > 1"
            class="photo-nav prev-photo"
            @click="previousPhoto"
            aria-label="Previous photo"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <button
            v-if="selectedUnit.unit_photos.length > 1"
            class="photo-nav next-photo"
            @click="nextPhoto"
            aria-label="Next photo"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
          <div
            v-if="selectedUnit.unit_photos.length > 1"
            class="photo-counter"
          >
            {{ currentPhotoIndex + 1 }} / {{ selectedUnit.unit_photos.length }}
          </div>
        </div>
        <div
          v-if="selectedUnit.unit_photos.length > 1"
          class="photo-thumbnails"
        >
          <div
            v-for="(photo, index) in selectedUnit.unit_photos"
            :key="index"
            class="thumbnail"
            :class="{ active: index === currentPhotoIndex }"
            @click="currentPhotoIndex = index"
          >
            <img
              :src="getThumbnailUrl(photo)"
              :alt="`Thumbnail ${index + 1}`"
            />
          </div>
        </div>
      </div>
      <div
        v-else
        class="no-photos"
      >
        <p>No photos available for this unit.</p>
      </div>
    </Dialog>

    <!-- Photos Section -->
    <div class="grid grid-nogutter mt-5">
      <div
        v-for="(image, index) in showcaseImages"
        :key="index"
        class="p-0 col-12 md:col-4"
      >
        <img
          :src="image.src"
          :alt="image.alt"
          class="showcase-image"
        />
      </div>
    </div>

    <!-- Application Process Info Section -->
    <div class="container mt-5 mb-5">
      <div class="grid">
        <div class="col-12 md:col-6">
          <div class="info-block">
            <h3 class="text-primary">What You'll Need</h3>
            <ul class="info-list">
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Valid government-issued ID</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Proof of income (last 2-3 pay stubs)</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Previous rental history and references</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Application fee (varies by property)</p>
              </li>
            </ul>
          </div>
        </div>
        <div class="col-12 md:col-6">
          <div class="info-block">
            <h3 class="text-primary">Application Process</h3>
            <ul class="info-list">
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Submit your application online (takes 10-15 minutes)</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Our team reviews your application</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Background and credit check processing</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Approval notification within 24-48 hours (typically)</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Sign lease and schedule move-in date</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Key Benefits Section -->
    <div class="container mt-5 mb-5">
      <div class="grid">
        <div
          v-for="(benefit, index) in keyBenefits"
          :key="index"
          class="col-12 md:col-4"
        >
          <div class="info-block">
            <div class="benefit-icon-small">
              <i :class="benefit.icon"></i>
            </div>
            <h4 class="text-primary text-center">{{ benefit.title }}</h4>
            <p class="text-center">{{ benefit.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Call to Action -->
    <banner
      :backgroundImage="$maxxpm.config.home.midContainerImage"
      class="cta-banner"
    >
      <h3 class="text-white mt-0 text-center">Ready to Apply?</h3>
      <p class="text-white text-center cta-subtitle">Don't wait - start your application today and take the first step toward your new home!</p>
    </banner>

    <!-- Contact Info -->
    <div class="container mt-5 mb-5">
      <div class="contact-info">
        <h3 class="text-primary text-center">Questions About Applying?</h3>
        <p class="text-center">
          Call us at
          <a
            href="tel:+13852870208"
            class="text-primary"
            @click="event('contact_us_phone')"
            >{{ $maxxpm.config.footer.phone }}</a
          >
          or
          <a
            href="mailto:info@maxxpm.com"
            class="text-primary"
            @click="event('contact_us_email')"
            >email us</a
          >.
        </p>
        <p class="text-center">
          {{ $maxxpm.config.footer.address }},
          {{ $maxxpm.config.footer.cityStateZip }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-env browser */
import { inject, ref, onBeforeMount } from "vue";
import { useRoute } from "vue-router";
import { Banner } from "@maxxpm/vue-components";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import axios from "axios";
import { event } from "vue-gtag";

const $maxxpm = inject("$maxxpm");
const route = useRoute();

const postedUnits = ref([]);
const loadingUnits = ref(true);
const photoModalVisible = ref(false);
const selectedUnit = ref(null);
const currentPhotoIndex = ref(0);

const getListings = async (limit = 10, offset = 0, listings = []) => {
  const posted = await axios.get(`https://09udmvr09d.execute-api.us-west-2.amazonaws.com/listings?propertyGroup=${$maxxpm.config.availability.propertyGroup}&limit=${limit}&offset=${offset}`);

  if (posted.data.pagination.hasMore) {
    return await getListings(posted.data.pagination.limit, posted.data.pagination.offset, [...listings, ...posted.data.data]);
  }
  return [...listings, ...posted.data.data];
};

onBeforeMount(async () => {
  try {
    const posted = await getListings();
    postedUnits.value = posted;
  } catch (error) {
    console.error("Error fetching listings:", error);
    postedUnits.value = [];
  } finally {
    loadingUnits.value = false;
  }
});

const formatAddress = (unit) => {
  const parts = [];
  if (unit.address1) parts.push(unit.address1);
  if (unit.address2) parts.push(unit.address2);
  if (unit.city) parts.push(unit.city);
  if (unit.state) parts.push(unit.state);
  if (unit.zip) parts.push(unit.zip);
  return parts.length > 0 ? parts.join(", ") : "Unit";
};

const openPhotoModal = (unit) => {
  selectedUnit.value = unit;
  currentPhotoIndex.value = 0;
  photoModalVisible.value = true;
};

const closePhotoModal = () => {
  photoModalVisible.value = false;
  selectedUnit.value = null;
  currentPhotoIndex.value = 0;
};

const nextPhoto = () => {
  if (selectedUnit.value && selectedUnit.value.unit_photos && currentPhotoIndex.value < selectedUnit.value.unit_photos.length - 1) {
    currentPhotoIndex.value++;
  }
};

const previousPhoto = () => {
  if (currentPhotoIndex.value > 0) {
    currentPhotoIndex.value--;
  }
};

const getUtmSource = () => {
  // eslint-disable-next-line no-undef
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("utm_source") || route.query.utm_source || "";
};

const buildApplicationUrl = (unit) => {
  if (!unit || !unit.id) return "";
  const utmSource = getUtmSource();
  const baseUrl = `https://maxxpm.appfolio.com/apply/${unit.id}/start`;
  return utmSource ? `${baseUrl}?source=${encodeURIComponent(utmSource)}` : baseUrl;
};

const openApplicationUrl = (unit) => {
  const url = buildApplicationUrl(unit);
  if (url) {
    // eslint-disable-next-line no-undef
    window.open(url, "_blank");
    event("apply_now");
  }
};

const getPhotoUrl = (photo) => {
  if (!photo) return "";
  if (typeof photo === "string") return photo;
  return photo.url || photo.original_url || photo.thumbnail_url || "";
};

const getThumbnailUrl = (photo) => {
  if (!photo) return "";
  if (typeof photo === "string") return photo;
  return photo.thumbnail_url || photo.url || photo.original_url || "";
};

const scrollToAvailableUnits = () => {
  // eslint-disable-next-line no-undef
  const element = document.getElementById("available-units");
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const keyBenefits = [
  {
    icon: "fas fa-mobile-alt",
    title: "Easy Online Process",
    description: "Complete your application online in minutes from any device. Apply from anywhere, anytime with our streamlined digital application.",
  },
  {
    icon: "fas fa-shield-alt",
    title: "Secure & Safe",
    description: "Your personal information is protected with bank-level encryption and industry-standard security measures.",
  },
  {
    icon: "fas fa-clock",
    title: "Fast Approval",
    description: "Quick turnaround time - typically approved within 24-48 hours so you can move in sooner.",
  },
];

const showcaseImages = [
  {
    src: "https://cdn.maxxpm.com/abingtonheights/Abington%20Website%20Photos/23-web-or-mls-3712%20S%201940%20W-20.jpg",
    alt: "Abington Heights living space",
  },
  {
    src: "https://cdn.maxxpm.com/abingtonheights/Abington%20Website%20Photos/37-web-or-mls-3712%20S%201940%20W-34.jpg",
    alt: "Abington Heights kitchen",
  },
  {
    src: "https://cdn.maxxpm.com/abingtonheights/Abington%20Website%20Photos/34-web-or-mls-3712%20S%201940%20W-31.jpg",
    alt: "Abington Heights interior",
  },
];
</script>

<style lang="scss" scoped>
.applications-landing {
  width: 100%;
  min-height: 100vh;
}

.container {
  width: 95vw;
  max-width: 1200px;
  margin: auto;
}

// Hero Banner
:deep(.hero-banner) {
  position: relative;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;

  // Add dark overlay for better text readability
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 800px;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.hero-logo {
  max-width: 300px;
  max-height: 150px;
  width: auto;
  height: auto;
  margin-bottom: 1.5rem;
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;
}

.hero-subtitle {
  font-size: 2rem;
  font-weight: 400;
  color: var(--appTextColor);
  margin-bottom: 1rem;
}

.hero-description {
  font-size: 1.25rem;
  color: var(--appTextColor);
  margin-bottom: 2rem;
}

.hero-cta-button {
  :deep(.p-button) {
    padding: 1rem 2.5rem;
    font-size: 1.1rem;
    font-weight: 600;
    background: #faab4c;
    border: none;
    color: white;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
    transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;

    &:hover {
      background: #e89a3d;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
    }

    &:active {
      transform: translateY(0);
      background: #d88a2e;
    }
  }
}

// Key Benefits
.benefit-card {
  padding: 1.5rem;

  .benefit-icon {
    width: 80px;
    height: 80px;
    background: var(--primary-color);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;

    i {
      font-size: 2.5rem;
      color: white;
    }
  }

  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1rem;
    line-height: 1.6;
  }
}

.benefit-icon-small {
  width: 60px;
  height: 60px;
  background: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;

  i {
    font-size: 1.75rem;
    color: white;
  }
}

// Showcase Images
.showcase-image {
  width: 100%;
  height: 380px;
  object-fit: cover !important;
}

// Info Blocks
.info-block {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  height: 100%;

  h3,
  h4 {
    font-size: 1.75rem;
    margin-bottom: 1.5rem;
    text-align: center;
  }

  h4 {
    font-size: 1.5rem;
  }
}

.info-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    margin-bottom: 1rem;
  }

  p {
    margin: 0;
    line-height: 1.6;
  }
}

.li-marker {
  max-width: 35px;
  flex-shrink: 0;
}

// CTA Banner
:deep(.cta-banner) {
  height: 400px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;

  h3 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }

  .cta-subtitle {
    font-size: 1.5rem;
    margin: 0;
  }
}

// Contact Info
.contact-info {
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 1.75rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }

  a {
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
}

// Available Units Section
.available-units-description {
  font-size: 1.1rem;
  color: var(--appTextColor);
}

.listings-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    gap: 1.5rem;
    max-width: 100%;
  }
}

.unit-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  width: calc(33.333% - 1.5rem);
  min-width: 300px;
  max-width: calc(33.333% - 1.5rem);
  align-self: stretch;

  @media (max-width: 1024px) {
    width: calc(50% - 1rem);
    max-width: calc(50% - 1rem);
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    min-width: auto;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }
}

.unit-image-container {
  position: relative;
  width: 100%;
  height: 250px;
  overflow: hidden;
  cursor: pointer;
  background: #f8f9fa;

  .unit-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover .unit-image {
    transform: scale(1.05);
  }

  .unit-image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    background: #f8f9fa;

    i {
      font-size: 3rem;
      margin-bottom: 0.5rem;
    }

    p {
      margin: 0;
      font-size: 1rem;
    }
  }

  .unit-image-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
    color: white;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    opacity: 0;
    transition: opacity 0.3s ease;

    i {
      font-size: 1.2rem;
    }
  }

  &:hover .unit-image-overlay {
    opacity: 1;
  }
}

.unit-info {
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;

  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: var(--primary-color);
  }
}

.unit-details {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
}

.unit-detail {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--appTextColor);

  i {
    color: var(--primary-color);
  }
}

.unit-rent {
  margin-bottom: 1rem;

  .rent-amount {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--primary-color);
  }

  .rent-period {
    font-size: 1rem;
    color: var(--appTextColor);
  }
}

.apply-button {
  margin-top: auto;
  width: 100%;

  :deep(.p-button) {
    width: 100%;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
  }
}

.units-loading {
  color: var(--primary-color);
  font-size: 3rem;
  margin: 3rem 0;
}

// Photo Modal
:deep(.photo-modal) {
  .p-dialog-header {
    padding: 1.5rem;
  }

  .p-dialog-content {
    padding: 0;
  }
}

.photo-gallery {
  display: flex;
  flex-direction: column;
}

.main-photo-container {
  position: relative;
  width: 100%;
  background: #000;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;

  .main-photo {
    max-width: 100%;
    max-height: 70vh;
    width: auto;
    height: auto;
    object-fit: contain;
  }

  .photo-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.9);
    border: none;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.3s ease;
    z-index: 10;

    &:hover {
      background: rgba(255, 255, 255, 1);
    }

    i {
      font-size: 1.5rem;
      color: var(--primary-color);
    }

    &.prev-photo {
      left: 1rem;
    }

    &.next-photo {
      right: 1rem;
    }
  }

  .photo-counter {
    position: absolute;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
  }
}

.photo-thumbnails {
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  overflow-x: auto;
  background: #f8f9fa;

  .thumbnail {
    min-width: 100px;
    width: 100px;
    height: 100px;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    border: 3px solid transparent;
    transition: border-color 0.3s ease, transform 0.3s ease;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &:hover {
      transform: scale(1.05);
    }

    &.active {
      border-color: var(--primary-color);
    }
  }
}

.no-photos {
  padding: 3rem;
  text-align: center;
  color: var(--appTextColor);
}

// Responsive Design
@media (max-width: 768px) {
  :deep(.hero-banner) {
    min-height: 400px;
    padding: 2rem 1rem;
  }

  .hero-content {
    padding: 1.5rem;
    max-width: 100%;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.5rem;
  }

  .hero-description {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }

  .hero-logo {
    max-width: 200px;
    max-height: 100px;
    margin-bottom: 1rem;
  }

  .hero-cta-button {
    :deep(.p-button) {
      padding: 0.875rem 2rem;
      font-size: 1rem;
      width: 100%;
    }
  }

  .benefit-card {
    margin-bottom: 2rem;
  }

  :deep(.cta-banner) {
    height: 300px;

    h3 {
      font-size: 1.75rem;
    }

    .cta-subtitle {
      font-size: 1.2rem;
    }
  }

  .info-block {
    margin-bottom: 2rem;
  }

  .unit-image-container {
    height: 200px;
  }

  .unit-info {
    padding: 1rem;
  }

  .main-photo-container {
    min-height: 300px;

    .main-photo {
      max-height: 50vh;
    }

    .photo-nav {
      width: 40px;
      height: 40px;

      i {
        font-size: 1.2rem;
      }

      &.prev-photo {
        left: 0.5rem;
      }

      &.next-photo {
        right: 0.5rem;
      }
    }
  }

  .photo-thumbnails {
    padding: 0.5rem;

    .thumbnail {
      min-width: 80px;
      width: 80px;
      height: 80px;
    }
  }
}
</style>
